// src/Routes/password-reset/page.tsx
import { gql, useLazyQuery, useMutation } from "@apollo/client";
import React, { useState } from "react";

import { useNavigate } from "react-router-dom";
import { notify, notifyError } from "../../utils/toast";
import BackArrow from "../../components/common/back-arrow";
import FadeInWrapper from "../../components/common/fade-in-wrapper";

const FIND_EMAIL = gql`
  query findEmail($email: String!) {
    findEmail(email: $email) {
      ok
      error
    }
  }
`;

const SEND_AUTH_NUMBER_FOR_RESET = gql`
  mutation sendAuthNumberForReset($phoneNumber: String!) {
    sendAuthNumberForReset(phoneNumber: $phoneNumber) {
      ok
      error
    }
  }
`;
const VERIFY_AUTH_NUMBER_FOR_RESET = gql`
  mutation VerifyAuthNumberForReset(
    $phoneNumber: String!
    $authNumber: String!
  ) {
    verifyAuthNumberForReset(
      phoneNumber: $phoneNumber
      authNumber: $authNumber
    ) {
      ok
      error
    }
  }
`;
const CHANGE_PASSWORD = gql`
  mutation UpdateUserPasswordForReset($email: String!, $newPassword: String!) {
    updateUserPasswordForReset(email: $email, newPassword: $newPassword) {
      ok
      error
    }
  }
`;

const PasswordReset = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [isAuthNumberVerified, setIsAuthNumberVerified] = useState(false);
  const [isEmailVerified, setIsEmailVerified] = useState(false); // 이메일 인증 상태 추가
  const [emailError, setEmailError] = useState(""); // 이메일 오류 상태 추가
  const [phoneError, setPhoneError] = useState("");
  const [passwordConfirmError, setPasswordConfirmError] = useState("");
  const [findEmail] = useLazyQuery(FIND_EMAIL);
  const [sendAuthNumber] = useMutation(SEND_AUTH_NUMBER_FOR_RESET);
  const [verifyAuthNumber] = useMutation(VERIFY_AUTH_NUMBER_FOR_RESET);
  const [changePassword] = useMutation(CHANGE_PASSWORD);

  const validateEmail = (email: string) => {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/; // 이메일 정규 표현식
    return emailPattern.test(email);
  };

  const handleEmailVerification = async () => {
    if (!validateEmail(email)) {
      setEmailError("유효한 이메일 주소를 입력하세요."); // 이메일 오류 메시지 설정
      return;
    }
    setEmailError(""); // 오류 메시지 초기화
    const response = await findEmail({ variables: { email } });
    if (!response.data.findEmail.ok) {
      setEmailError(response.data.findEmail.error);
      return;
    }
    setIsEmailVerified(true); // 이메일 인증 상태 업데이트
    notify("이메일이 인증되었습니다."); // 실제 로직으로 대체 필요
  };

  const handleSendCode = async () => {
    if (isCodeSent) return; // 이미 인증번호가 발송된 경우 함수 종료
    if (phoneNumber.length !== 11) {
      setPhoneError("핸드폰 번호는 11자리여야 합니다.");
      return;
    }
    setPhoneError("");
    // 인증번호 발송 로직 추가
    const response = await sendAuthNumber({ variables: { phoneNumber } });
    if (!response.data.sendAuthNumberForReset.ok) {
      notifyError(response.data.sendAuthNumberForReset.error);
      return;
    }
    notify("인증번호가 발송되었습니다."); // 실제 로직으로 대체 필요
    setIsCodeSent(true); // 인증번호 발송 상태 업데이트
  };

  const handleVerifyAuthNumber = async () => {
    const response = await verifyAuthNumber({
      variables: { phoneNumber, authNumber: verificationCode },
    });
    if (response.data.verifyAuthNumberForReset.ok) {
      notify("인증이 완료되었습니다. 비밀번호를 재설정해주세요.");
      setIsAuthNumberVerified(true); // 인증번호 확인 상태 업데이트
    } else {
      notifyError(response.data.verifyAuthNumberForReset.error);
    }
  };

  const handlePasswordReset = async () => {
    if (password !== passwordConfirm) {
      setPasswordConfirmError("비밀번호가 일치하지 않습니다.");
      return;
    }
    setPasswordConfirmError("");
    setIsSubmitting(true);
    await changePassword({
      variables: { email, newPassword: password },
      onCompleted: (data) => {
        if (data.updateUserPasswordForReset.ok) {
          notify("비밀번호가 재설정되었습니다.");
          navigate("/login");
        } else {
          notifyError(data.updateUserPasswordForReset.error);
        }
      },
      onError: (error) => notifyError(error.message),
    });
    setIsSubmitting(false);
  };

  return (
    <div className="flex items-center justify-center min-h-screen p-4">
      <FadeInWrapper className="w-full max-w-md bg-white p-6 pb-10">
        <div className="mb-4" onClick={() => navigate(-1)}>
          <BackArrow />
        </div>
        <h2 className="text-2xl font-semibold text-center text-black mb-6">
          비밀번호 찾기
        </h2>

        {!isEmailVerified ? (
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-black mb-2 pl-2"
            >
              이메일
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (emailError) setEmailError("");
              }}
              required
              className="w-full px-4 py-4 border border-gray-300 rounded-xl focus:outline-none focus:border-primary"
              placeholder="이메일을 입력하세요."
            />
            {emailError && (
              <p className="text-red-500 text-sm mt-1 pl-2">{emailError}</p>
            )}
            <button
              type="button"
              onClick={handleEmailVerification}
              className="w-full py-2 mt-4 bg-primary font-bold text-white rounded-xl hover:brightness-90 transition duration-150"
            >
              이메일 인증하기
            </button>
          </div>
        ) : !isAuthNumberVerified ? (
          <div className="space-y-4">
            <div>
              <label
                htmlFor="phoneNumber"
                className="block text-sm font-medium text-black mb-2 pl-2"
              >
                핸드폰 번호
              </label>
              <input
                type="text"
                id="phoneNumber"
                value={phoneNumber}
                onChange={(e) => {
                  setPhoneNumber(e.target.value);
                  if (phoneError) setPhoneError("");
                }}
                required
                className="w-full px-4 py-4 border border-gray-300 rounded-xl focus:outline-none focus:border-primary"
                placeholder="핸드폰 번호를 입력하세요."
                maxLength={11} // 핸드폰 번호 최대 길이 설정
              />
              {phoneError && (
                <p className="text-red-500 text-sm mt-1 pl-2">{phoneError}</p>
              )}
            </div>

            <div>
              <button
                type="button"
                onClick={handleSendCode}
                className={`w-full py-2 rounded-xl text-white font-bold ${
                  isCodeSent
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-primary hover:brightness-90 transition duration-150"
                }`}
                disabled={isCodeSent || phoneNumber.length !== 11} // 인증번호가 발송된 경우 버튼 비활성화
              >
                인증번호 발송
              </button>
            </div>

            {isCodeSent && (
              <>
                <div>
                  <label
                    htmlFor="verificationCode"
                    className="block text-sm font-medium text-black mb-2 pl-2"
                  >
                    인증번호
                  </label>
                  <input
                    type="text"
                    id="verificationCode"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value)}
                    required
                    className="w-full px-4 py-4 border border-gray-300 rounded-xl focus:outline-none focus:border-primary"
                    placeholder="인증번호를 입력하세요."
                    maxLength={6} // 인증번호 최대 길이 설정
                  />
                </div>
                <button
                  type="button"
                  onClick={handleVerifyAuthNumber}
                  className={`w-full py-2 rounded-xl text-white font-bold ${
                    isSubmitting
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-primary hover:brightness-90 transition duration-150"
                  }`}
                  disabled={isSubmitting || verificationCode.length !== 6} // 인증번호 길이 확인
                >
                  인증하기
                </button>
              </>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label
                htmlFor="newPassword"
                className="block text-sm font-medium text-black mb-2 pl-2"
              >
                새로운 비밀번호
              </label>
              <input
                type="password"
                id="newPassword"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-4 py-4 border border-gray-300 rounded-xl focus:outline-none focus:border-primary"
                placeholder="새로운 비밀번호를 입력하세요."
              />
            </div>
            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-medium text-black mb-2 pl-2"
              >
                비밀번호 확인
              </label>
              <input
                type="password"
                id="confirmPassword"
                value={passwordConfirm}
                onChange={(e) => {
                  setPasswordConfirm(e.target.value);
                  if (passwordConfirmError) setPasswordConfirmError("");
                }}
                required
                className="w-full px-4 py-4 border border-gray-300 rounded-xl focus:outline-none focus:border-primary"
                placeholder="비밀번호를 다시 입력하세요."
              />
              {passwordConfirmError && (
                <p className="text-red-500 text-sm mt-1 pl-2">
                  {passwordConfirmError}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={handlePasswordReset}
              className={`w-full py-2 rounded-xl text-white font-bold ${
                isSubmitting
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-primary hover:brightness-90 transition duration-150"
              }`}
              disabled={isSubmitting}
            >
              비밀번호 재설정
            </button>
          </div>
        )}
      </FadeInWrapper>
    </div>
  );
};

export default PasswordReset;
